/**
 * Global Reporting Dashboard
 * Cross-project analytics with 6-status workflow support
 */

import { useState, useEffect, useMemo } from 'react';
import {
  FileText,
  TrendingUp,
  Filter,
  RefreshCw,
  AlertTriangle,
  Timer,
} from 'lucide-react';
import { useInterval } from '@/hooks/useInterval';
import { reportsApi } from '@/services/reports';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/LoadingSpinner';
import { AppHeader } from '@/components/AppHeader';
import { StatusBar } from '@/components/StatusBar';
import { GlobalExportsPanel } from '@/components/GlobalExportsPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Label } from '@/components/ui/label';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '@/lib/utils';
import {
  STATUS_ORDER,
  getStatusConfig,
  type TestCaseStatus,
} from '@/lib/status-config';
import type { GlobalReportResponse } from '@/types/api';

// =============================================================================
// Chart Colors (from status-config)
// =============================================================================

const STATUS_CHART_COLORS: Record<TestCaseStatus, string> = {
  new: 'hsl(var(--muted-foreground))',
  ongoing: 'hsl(var(--info))',
  errors: 'hsl(var(--destructive))',
  retest: 'hsl(var(--warning))',
  ready_to_validate: 'hsl(var(--review))',
  closed: 'hsl(var(--success))',
};

const DOMAIN_PIE_COLORS = [
  'hsl(var(--lotus-red))',
  'hsl(var(--info))',
  'hsl(var(--warning))',
  'hsl(var(--success))',
  'hsl(var(--review))',
  'hsl(var(--destructive))',
  'hsl(var(--lotus-brown))',
  'hsl(var(--lotus-caramel))',
];

// =============================================================================
// Sub-components
// =============================================================================

interface KPICardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

function KPICard({ title, value, icon, color, subtitle }: KPICardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={cn('p-2 rounded-lg', color)}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold">{value}</div>
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export default function Reporting() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [dateRange, setDateRange] = useState<string>('30');
  const [showFilters, setShowFilters] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [reportData, setReportData] = useState<GlobalReportResponse | null>(null);

  // Fetch global report data
  const fetchReport = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      else setIsRefreshing(true);

      const params: { status?: 'all' | 'active' | 'inactive'; days?: number } = {
        status: statusFilter,
      };
      if (dateRange !== 'all') {
        params.days = parseInt(dateRange);
      }

      const data = await reportsApi.getGlobalReport(params);
      setReportData(data);
      setLastRefresh(new Date());
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      if (!silent) {
        toast.error(`Failed to load report: ${message}`);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Fetch on mount and when filters change
  useEffect(() => {
    fetchReport();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateRange]);

  // Automatic polling every 30 seconds
  useInterval({
    callback: () => fetchReport(true),
    interval: 30000,
    enabled: !isLoading,
    pauseWhenHidden: true,
  });

  // Derived chart data
  const userChartData = useMemo(() => {
    if (!reportData?.by_user) return [];
    return Object.entries(reportData.by_user).map(([user, stats]) => ({
      user: user.length > 15 ? user.slice(0, 15) + '...' : user,
      ...stats,
    }));
  }, [reportData?.by_user]);

  const domainPieData = useMemo(() => {
    if (!reportData?.by_domain) return [];
    return Object.entries(reportData.by_domain).map(([name, stats], i) => ({
      name,
      value: stats.total_steps,
      color: DOMAIN_PIE_COLORS[i % DOMAIN_PIE_COLORS.length],
    }));
  }, [reportData?.by_domain]);

  const statusDistributionData = useMemo(() => {
    if (!reportData?.overview) return [];
    const ov = reportData.overview;
    return STATUS_ORDER.filter(s => (ov[s] ?? 0) > 0).map(s => ({
      name: getStatusConfig(s).label,
      value: ov[s] ?? 0,
      color: STATUS_CHART_COLORS[s],
    }));
  }, [reportData?.overview]);

  const formatLastRefresh = () => {
    if (!lastRefresh) return 'Never';
    const diff = Math.floor((Date.now() - lastRefresh.getTime()) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return lastRefresh.toLocaleTimeString();
  };

  // Loading state
  if (isLoading && !reportData) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <AppHeader title="Global Reporting" showBack backPath="/admin/projects" />
        <main className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-muted-foreground">Loading report...</p>
          </div>
        </main>
      </div>
    );
  }

  const overview = reportData?.overview;
  const projects = reportData?.projects ?? [];
  const userCount = reportData?.by_user ? Object.keys(reportData.by_user).length : 0;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader title="Global Reporting" showBack backPath="/admin/projects" />

      <main className="flex-1 px-6 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-display font-bold">Global Reporting</h1>
              <p className="text-muted-foreground mt-1">
                Cross-project analytics and performance metrics
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchReport()}
                disabled={isRefreshing || isLoading}
                className="gap-2"
              >
                <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="gap-2"
              >
                <Filter className="h-4 w-4" />
                {showFilters ? 'Hide Filters' : 'Filters'}
              </Button>
            </div>
          </div>

          {/* Last Updated */}
          {lastRefresh && (
            <div className="flex items-center justify-end text-sm text-muted-foreground">
              <span>Last updated: {formatLastRefresh()}</span>
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <Card>
              <CardHeader>
                <CardTitle>Filters</CardTitle>
                <CardDescription>Refine the data shown in reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="status">Project Status</Label>
                    <SearchableSelect
                      id="status"
                      value={statusFilter}
                      onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}
                      options={[
                        { value: 'all', label: 'All Projects' },
                        { value: 'active', label: 'Active Only' },
                        { value: 'inactive', label: 'Inactive Only' },
                      ]}
                      placeholder="Select status"
                      searchPlaceholder="Search..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateRange">Date Range</Label>
                    <SearchableSelect
                      id="dateRange"
                      value={dateRange}
                      onValueChange={setDateRange}
                      options={[
                        { value: '7', label: 'Last 7 days' },
                        { value: '30', label: 'Last 30 days' },
                        { value: '90', label: 'Last 90 days' },
                        { value: 'all', label: 'All time' },
                      ]}
                      placeholder="Select range"
                      searchPlaceholder="Search..."
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="Total Steps"
              value={overview?.total_steps ?? 0}
              subtitle={`${overview?.total_scenarios ?? 0} scenarios across ${overview?.total_projects ?? 0} projects`}
              icon={<FileText className="h-5 w-5 text-gray-600" />}
              color="bg-gray-100"
            />
            <KPICard
              title="Progress"
              value={`${overview?.progress_percentage ?? 0}%`}
              subtitle={`${(overview?.ready_to_validate ?? 0) + (overview?.closed ?? 0)} of ${overview?.total_steps ?? 0} done`}
              icon={<TrendingUp className="h-5 w-5 text-green-600" />}
              color="bg-green-100"
            />
            <KPICard
              title="Needs Action"
              value={(overview?.errors ?? 0) + (overview?.retest ?? 0)}
              subtitle="Errors + Retest"
              icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
              color="bg-red-100"
            />
            <KPICard
              title="Active"
              value={overview?.ongoing ?? 0}
              subtitle="Currently ongoing"
              icon={<Timer className="h-5 w-5 text-blue-600" />}
              color="bg-blue-100"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Steps by User (stacked bar) */}
            <Card>
              <CardHeader>
                <CardTitle>Steps by Test User</CardTitle>
                <CardDescription>
                  Status distribution per tester
                </CardDescription>
              </CardHeader>
              <CardContent>
                {userChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={userChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="user" tick={{ fontSize: 11 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="closed" fill={STATUS_CHART_COLORS.closed} name="Closed" stackId="a" />
                      <Bar dataKey="ready_to_validate" fill={STATUS_CHART_COLORS.ready_to_validate} name="Validate" stackId="a" />
                      <Bar dataKey="ongoing" fill={STATUS_CHART_COLORS.ongoing} name="Ongoing" stackId="a" />
                      <Bar dataKey="retest" fill={STATUS_CHART_COLORS.retest} name="Retest" stackId="a" />
                      <Bar dataKey="errors" fill={STATUS_CHART_COLORS.errors} name="Errors" stackId="a" />
                      <Bar dataKey="new" fill={STATUS_CHART_COLORS.new} name="New" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No user data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Steps by Domain (pie) */}
            <Card>
              <CardHeader>
                <CardTitle>Steps by Domain</CardTitle>
                <CardDescription>Distribution across business domains</CardDescription>
              </CardHeader>
              <CardContent>
                {domainPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={domainPieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) =>
                          `${name} ${(percent * 100).toFixed(0)}%`
                        }
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {domainPieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No domain data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Distribution (donut) */}
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
                <CardDescription>Overall test step status breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {statusDistributionData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={statusDistributionData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {statusDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No status data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Project Progress (horizontal stacked bars) */}
            <Card>
              <CardHeader>
                <CardTitle>Project Progress</CardTitle>
                <CardDescription>Status breakdown per project</CardDescription>
              </CardHeader>
              <CardContent>
                {projects.length > 0 ? (
                  <div className="space-y-4">
                    {projects.map((project) => (
                      <div key={project.project_id} className="space-y-1.5">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium truncate mr-2">{project.project_number} - {project.name}</span>
                          <span className="text-muted-foreground shrink-0">
                            {project.progress_percentage}%
                          </span>
                        </div>
                        <StatusBar
                          counts={{
                            new: project.new,
                            ongoing: project.ongoing,
                            errors: project.errors,
                            retest: project.retest,
                            ready_to_validate: project.ready_to_validate,
                            closed: project.closed,
                          }}
                          total={project.total_steps}
                          height="h-2.5"
                        />
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>{project.scenario_count} scenarios</span>
                          <span>&middot;</span>
                          <span>{project.total_steps} steps</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-[200px] text-muted-foreground">
                    No projects found
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Completion Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {overview?.progress_percentage ?? 0}%
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Of all test steps done (validated + closed)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Avg. Steps per Tester</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {userCount > 0
                    ? ((overview?.total_steps ?? 0) / userCount).toFixed(1)
                    : '0'}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Average workload distribution
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Active Testers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{userCount}</div>
                <p className="text-sm text-muted-foreground mt-1">
                  Testers with assigned steps
                </p>
              </CardContent>
            </Card>
          </div>

          <GlobalExportsPanel />
        </div>
      </main>

      <footer className="w-full py-4 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} Lotus Bakeries. Internal Testing Portal.
          </p>
        </div>
      </footer>
    </div>
  );
}
